/* Console-scoped auth — separate token key from the marketing /admin auth.
   The console token lives at `lc_console_token` and is sent on every /api/console/*
   call. Dipali's existing login response carries roles=["admin","concierge_admin"]
   so we hit the same /api/auth/login endpoint but store the token in a different
   localStorage key to keep the two surfaces fully independent. */
import axios from "axios";
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

import { BACKEND_URL } from "./backend-url";
export const CONSOLE_API_BASE = `${BACKEND_URL}/api`;

export const consoleApi = axios.create({
    baseURL: CONSOLE_API_BASE,
    withCredentials: false,
});

consoleApi.interceptors.request.use((config) => {
    const token = localStorage.getItem("lc_console_token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

const ConsoleAuthCtx = createContext(null);

export function ConsoleAuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        const token = localStorage.getItem("lc_console_token");
        if (!token) {
            setUser(false);
            setLoading(false);
            return;
        }
        try {
            const { data } = await consoleApi.get("/console/me");
            if (!data.roles?.includes("concierge_admin") && !data.roles?.includes("admin")) {
                localStorage.removeItem("lc_console_token");
                setUser(false);
            } else {
                setUser(data);
            }
        } catch {
            localStorage.removeItem("lc_console_token");
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        try {
            const { data } = await consoleApi.post("/auth/login", { email, password });
            if (!data.roles?.includes("concierge_admin") && !data.roles?.includes("admin")) {
                return { ok: false, error: "This account does not have Console access." };
            }
            localStorage.setItem("lc_console_token", data.access_token);
            // Hit /console/me to populate the rich session
            const me = await consoleApi.get("/console/me");
            setUser(me.data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: e.response?.data?.detail || e.message };
        }
    };

    const logout = () => {
        localStorage.removeItem("lc_console_token");
        setUser(false);
    };

    return (
        <ConsoleAuthCtx.Provider value={{ user, loading, login, logout, refresh }}>
            {children}
        </ConsoleAuthCtx.Provider>
    );
}

export function useConsoleAuth() {
    return useContext(ConsoleAuthCtx);
}
